import React, { useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";

// ClickWatcher
// ---------------------------------------------------
// Listens for clicks outside of its root node.
// Useful for when you want to write a dropdown menu,
// or any other instance where you want to keep track
// of clicks outside a node.
//
// Dynamically adds/removes click handlers when
// "active" prop becomes true/false.
//
// Author's note: here be gotchas.
// If the element of the outside click stopPropogation's,
// there's no way for this component to track it, since
// it doesn't bubble to the document level.
//
// Ideally this would be resolved by having an overlay,
// which would catch stray clicks and prevent this issue.
// However, that would require either:
// A) being extremely confident in our z-indexes, or
// B) absolutely positioning the popovers, which is nice,
//    but hard.

const ClickWatcher = ({
	ElementType,
	id,
	className,
	onClick,
	children,
	active,
	onOutsideClick,
}) => {
	const rootNode = useRef(null);
	const onDocumentClick = useCallback((event) => {
		// Early exits—not active, no root node, or root node contains click.
		if (!active || !rootNode.current || rootNode.current.contains(event.target)) {
			return;
		}

		// Root node does not contain click, notify of outside click.
		if (onOutsideClick) {
			onOutsideClick(event);
		}
	}, [active, onOutsideClick]);
	const toggleClickListener = useCallback((addEventListener) => {
		// This listens for mousedown instead of click, as that's less
		// likely to suffer from stopPropagation issues.
		// We were running into an issue where this listener was being added
		// before the click event that triggered its addition was finished
		// bubbling up the DOM, so it would immediately trigger.
		// This only happens when the ClickWatcher trigger is outside the
		// ClickWatcher. The *right* solution is probably to render an
		// invisible overlay behind the menu and capture events there.
		// But for now, we have this.

		if (addEventListener) {
			document.addEventListener("mousedown", onDocumentClick);
		} else {
			document.removeEventListener("mousedown", onDocumentClick);
		}
	}, [onDocumentClick]);

	// Set the event listener as `active`. When active/onOutsideClick change,
	// tear down old listeners and setup new ones.
	useEffect(() => {
		toggleClickListener(active);

		return () => {
			toggleClickListener(false);
		};
	}, [active, onOutsideClick, toggleClickListener]);

	return (
		<ElementType ref={rootNode} id={id} className={className} onClick={onClick}>
			{children}
		</ElementType>
	);
};

ClickWatcher.propTypes = {
	ElementType: PropTypes.string,
	id: PropTypes.string,
	className: PropTypes.string,
	children: PropTypes.node,
	onClick: PropTypes.func,
	onOutsideClick: PropTypes.func.isRequired,
	active: PropTypes.bool,
};

ClickWatcher.defaultProps = {
	ElementType: "div",
	id: undefined,
	className: undefined,
	onClick: undefined,
	children: null,
	active: true,
};

export default ClickWatcher;



// WEBPACK FOOTER //
// ./src/shared/components/ClickWatcher.jsx